import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { Button, Layout, theme, Drawer, ConfigProvider, Dropdown, Select } from 'antd';
import { List, Bell, UserCircle, UsersThree, SignOut, CaretDown } from '@phosphor-icons/react';
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { ref, onValue } from "firebase/database";
import { firestore, database, auth } from './firebase';
import { FirebaseProvider, useFirebase } from './FirebaseContext';
import './components/AuthLayout.css';

import SideMenu from './components/SideMenu';
import Home from './pages/Home';
import Chat from './pages/Chat';
import Rozliczenie from './pages/Rozliczenie';
import SzablonyDokumentow from './pages/SzablonyDokumentow';
import MojeDokumenty from './pages/MojeDokumenty';
import Faktury from './pages/Faktury';
import Przelewy from './pages/Przelewy';
import Kierowcy from './pages/Kierowcy';
import Uzytkownicy from './pages/Uzytkownicy';
import Samochody from './pages/Samochody';
import Raport from './pages/Raport';
import FakturyZW from './pages/FakturyZW';
import Login from './pages/Login';
import AdminNotificationSender from './pages/AdminNotificationSender';
import Register from './pages/Register';
import Verifikacja from './pages/Verifikacja';
import Wydatki from './pages/Wydatki';
import BonusPartnera from './pages/BonusPartnera';
import logo from './assets/spario.png';
import plPL from 'antd/lib/locale/pl_PL';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import 'dayjs/locale/pl';
import ExportXMLComarch from './pages/ExportXMLComarch';
import Raporty from './pages/Raporty';
import KsefSettings from './pages/KsefSettings';

dayjs.extend(isoWeek);
dayjs.locale('pl');

const { Header, Sider, Content } = Layout;

const PAGE_TITLES = {
  '/chat': 'Chat z kierowcami',
  '/rozliczenie': 'Rozliczenie',
  '/faktury': 'Faktury',
  '/przelewy': 'Przelewy',
  '/kierowcy': 'Kierowcy',
  '/uzytkownicy': 'Użytkownicy',
  '/samochody': 'Samochody',
  '/raport': 'Raport',
  '/faktury-zw': 'Faktury ZW',
  '/powiadomienia': 'Powiadomienia',
  '/verifikacja': 'Weryfikuj użytkowników',
  '/wydatki': 'Wydatki',
  '/bonus': 'Bonus Partnera',
  '/export-xml-comarch': 'Eksport XML Comarch',
  '/raporty': 'Raporty',
  '/template': 'Szablony dokumentów',
  '/moje-dokumenty': 'Moje dokumenty',
  '/ksef': 'KSeF',
};

const AppHeader = ({ collapsed, setCollapsed, isMobile, setDrawerVisible }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentFirebase, currentProject, switchProject } = useFirebase();
  const [pendingUsersCount, setPendingUsersCount] = useState(0);
  const headerAuth = currentFirebase.auth;
  const firestore = currentFirebase.firestore;
  const userEmail = headerAuth?.currentUser?.email || '';

  useEffect(() => {
    if (!firestore) return;
    const q = query(collection(firestore, "users"), where("status", "==", "Oczekuje na weryfikacje"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setPendingUsersCount(snapshot.size);
    });
    return () => unsubscribe();
  }, [firestore, currentProject]);

  const pageTitle = PAGE_TITLES[location.pathname] || 'Dashboard';

  const [logoutLoading, setLogoutLoading] = useState(false);

  const handleLogout = async () => {
    setLogoutLoading(true);
    await signOut(headerAuth);
    setTimeout(() => {
      navigate('/login');
      setLogoutLoading(false);
    }, 1200);
  };

  const dropdownItems = {
    items: [
      {
        key: 'email',
        label: (
          <span style={{ color: '#9ca3af', fontSize: 12, cursor: 'default' }}>
            {userEmail}
          </span>
        ),
        disabled: true,
      },
      { type: 'divider' },
      {
        key: 'logout',
        label: 'Wyloguj się',
        icon: <SignOut size={16} />,
        onClick: handleLogout,
        danger: true,
      },
    ],
  };

  // Page-specific header tabs (e.g. Faktury / KSeF)
  const [headerTabs, setHeaderTabs] = useState(null);
  useEffect(() => {
    const sync = () => setHeaderTabs(window.__headerTabs ? [...window.__headerTabs] : null);
    sync();
    window.addEventListener('headerTabsChanged', sync);
    return () => window.removeEventListener('headerTabsChanged', sync);
  }, [location.pathname]);

  if (logoutLoading) {
    return (
      <div className="system-loading">
        <div className="system-loading-logo"><img src={logo} alt="Spario" /></div>
        <div className="system-loading-spinner" />
        <div className="system-loading-text">Wylogowywanie...</div>
      </div>
    );
  }

  return (
    <Header className={`app-header${headerTabs ? ' has-tabs' : ''}`}>
      <div className="app-header-row">
      <div className="app-header-left">
        {isMobile && (
          <button className="app-header-btn" onClick={() => setDrawerVisible(true)}>
            <List size={22} weight="bold" />
          </button>
        )}
        <h1 className="app-header-title">{pageTitle}</h1>
      </div>
      <div className="app-header-right">
        <Select
          value={currentProject}
          onChange={switchProject}
          className="app-header-project"
          size="small"
          variant="borderless"
          suffixIcon={<CaretDown size={12} weight="bold" />}
        >
          <Select.Option value="ColorPartner">ColorPartner</Select.Option>
          <Select.Option value="Spario">Spario</Select.Option>
        </Select>
        <div className="app-header-divider" />
        <button
          className="app-header-btn"
          onClick={() => navigate('/verifikacja')}
          style={{ position: 'relative' }}
        >
          <UsersThree size={20} weight="duotone" />
          {pendingUsersCount > 0 && (
            <span className="app-header-badge">{pendingUsersCount}</span>
          )}
        </button>
        <button className="app-header-btn">
          <Bell size={20} weight="duotone" />
        </button>
        <Dropdown menu={dropdownItems} placement="bottomRight" trigger={['click']}>
          <button className="app-header-avatar">
            <UserCircle size={28} weight="duotone" />
          </button>
        </Dropdown>
      </div>
      </div>
      {headerTabs && (
        <div className="app-header-tabs">
          {headerTabs.map((tab) => (
            <button
              key={tab.key}
              className={`app-header-tab${tab.active ? ' active' : ''}`}
              onClick={tab.onClick}
            >
              {tab.label}
              {tab.count != null && (
                <span className="app-header-tab-count">{tab.count}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </Header>
  );
};

const ProtectedRoute = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);
  if (loading) {
    return <div>Loading...</div>;
  }
  return user ? children : <Navigate to="/login" />;
};

const KsefProtectedRoute = ({ children }) => {
  const { currentFirebase } = useFirebase();
  const db = currentFirebase.database;
  const [hasAccess, setHasAccess] = useState(null);
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    const unsub = onAuthStateChanged(currentFirebase.auth, (user) => {
      setUserEmail(user?.email?.toLowerCase() || '');
    });
    return () => unsub();
  }, [currentFirebase.auth]);

  useEffect(() => {
    if (!userEmail) return;
    const accessRef = ref(db, 'ksefAccessEmails');
    const unsub = onValue(accessRef, (snapshot) => {
      if (snapshot.exists()) {
        const emails = Object.values(snapshot.val()).map(e => e.toLowerCase());
        setHasAccess(emails.includes(userEmail));
      } else {
        setHasAccess(false);
      }
    });
    return () => unsub();
  }, [db, userEmail]);

  if (hasAccess === null) return <div>Loading...</div>;
  return hasAccess ? children : <Navigate to="/" />;
};

const AppLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const { token: { colorBgContainer, borderRadiusLG } } = theme.useToken();
  const location = useLocation();
  const isChatPage = location.pathname === '/chat';

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleMenuClick = () => {
    if (isMobile) setDrawerVisible(false);
  };

  return (
    <Layout style={{ minHeight: '100vh', ...(isChatPage && { height: '100vh', maxHeight: '100vh', overflow: 'hidden' }) }}>
      {!isMobile ? (
        <Sider trigger={null} collapsible collapsed={collapsed} style={{ height: '100vh', background: '#fff', overflow: 'hidden', borderRight: '1px solid #f0f0f0' }}>
          <SideMenu collapsed={collapsed} onCollapse={() => setCollapsed(!collapsed)} onClick={handleMenuClick} />
        </Sider>
      ) : (
        <Drawer
          title="Menu"
          placement="left"
          onClose={() => setDrawerVisible(false)}
          visible={drawerVisible}
          bodyStyle={{ padding: 0 }}
          mode="inline"
        >
          <SideMenu collapsed={false} onClick={handleMenuClick} />
        </Drawer>
      )}
      <Layout style={isChatPage ? { height: '100vh', overflow: 'hidden' } : undefined}>
        <AppHeader collapsed={collapsed} setCollapsed={setCollapsed} isMobile={isMobile} setDrawerVisible={setDrawerVisible} />
        <Content style={{
          margin: '24px 16px',
          padding: 24,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
          display: 'flex',
          flexDirection: 'column',
          overflow: isChatPage ? 'hidden' : 'auto',
          ...(isChatPage && { flex: 1, minHeight: 0 }),
        }}>
          <Routes>
            <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
            <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
            <Route path="/rozliczenie" element={<ProtectedRoute><Rozliczenie /></ProtectedRoute>} />
            <Route path="/faktury" element={<ProtectedRoute><Faktury /></ProtectedRoute>} />
            <Route path="/przelewy" element={<ProtectedRoute><Przelewy /></ProtectedRoute>} />
            <Route path="/kierowcy" element={<ProtectedRoute><Kierowcy /></ProtectedRoute>} />
            <Route path="/uzytkownicy" element={<ProtectedRoute><Uzytkownicy /></ProtectedRoute>} />
            <Route path="/samochody" element={<ProtectedRoute><Samochody /></ProtectedRoute>} />
            <Route path="/raport" element={<ProtectedRoute><Raport /></ProtectedRoute>} />
            <Route path="/faktury-zw" element={<ProtectedRoute><FakturyZW /></ProtectedRoute>} />
            <Route path="/template" element={<ProtectedRoute><SzablonyDokumentow /></ProtectedRoute>} />
            <Route path="/moje-dokumenty" element={<ProtectedRoute><MojeDokumenty /></ProtectedRoute>} />
            <Route path="/export-xml-comarch" element={<ProtectedRoute><ExportXMLComarch /></ProtectedRoute>} />
            <Route path="/ksef" element={<ProtectedRoute><KsefProtectedRoute><KsefSettings /></KsefProtectedRoute></ProtectedRoute>} />

            <Route path="/powiadomienia" element={<ProtectedRoute><AdminNotificationSender /></ProtectedRoute>} />
            <Route path="/verifikacja" element={<ProtectedRoute><Verifikacja /></ProtectedRoute>} />
            <Route path="/wydatki" element={<ProtectedRoute><Wydatki /></ProtectedRoute>} />
            <Route path="/bonus" element={<ProtectedRoute><BonusPartnera /></ProtectedRoute>} />
            <Route path="/raporty" element={<ProtectedRoute><Raporty /></ProtectedRoute>} />
          </Routes>
        </Content>
      </Layout>
    </Layout>
  );
};

const App = () => {
  return (
    <ConfigProvider locale={plPL}>
      <FirebaseProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="*" element={<AppLayout />} />
          </Routes>
        </Router>
      </FirebaseProvider>
    </ConfigProvider>
  );
};

export default App;
